/**
 * Length Role - Format Pillar
 *
 * Formata valores de comprimento para apresentação.
 *
 * @example
 * import { formatLength } from '@attrx/role-morphic/length/format';
 *
 * formatLength('kilometer', 42.195);                      // "42.2 km"
 * formatLength('kilometer', 42.195, { verbose: true });   // "42.2 kilometers"
 * formatLength('meter', 1000, { locale: 'pt-BR' });       // "1.000 m"
 */

import { LENGTH_UNITS, type LengthUnit } from "./constants";

// =============================================================================
// TYPES
// =============================================================================

export interface LengthFormatOptions {
  /** Casas decimais (default: 2) */
  decimals?: number;
  /** Usa nome completo ao invés de símbolo */
  verbose?: boolean;
  /** Locale para formatação de número */
  locale?: string;
  /** Notação numérica */
  notation?: "standard" | "scientific" | "compact";
}

// =============================================================================
// FORMAT
// =============================================================================

/**
 * Formata valor de comprimento para apresentação
 *
 * @param variant - Unidade do valor
 * @param value - Valor numérico
 * @param options - Opções de formatação
 * @returns String formatada
 *
 * @example
 * formatLength('kilometer', 42.195);                        // "42.2 km"
 * formatLength('kilometer', 42.195, { verbose: true });     // "42.2 kilometers"
 * formatLength('kilometer', 1, { verbose: true });          // "1 kilometer"
 * formatLength('meter', 1000, { locale: 'pt-BR' });         // "1.000 m"
 * formatLength('meter', 1500, { decimals: 0 });             // "1500 m"
 */
export function formatLength(
  variant: LengthUnit,
  value: number,
  options: LengthFormatOptions = {}
): string {
  const { decimals = 2, verbose = false, locale, notation } = options;
  const unit = LENGTH_UNITS[variant];

  if (!unit) {
    throw new Error(`Unknown length variant: ${variant}`);
  }

  // Formata número
  let formattedValue: string;

  if (locale) {
    const formatOptions: Intl.NumberFormatOptions = {
      minimumFractionDigits: 0,
      maximumFractionDigits: decimals,
    };

    if (notation) {
      formatOptions.notation = notation;
    }

    formattedValue = value.toLocaleString(locale, formatOptions);
  } else {
    formattedValue = Number(value.toFixed(decimals)).toString();
  }

  // Escolhe símbolo ou nome
  const separator = unit.noSpace ? "" : " ";

  if (verbose) {
    const name = value === 1 ? unit.singular : unit.plural;
    return `${formattedValue} ${name || unit.symbol}`;
  }

  return `${formattedValue}${separator}${unit.symbol}`;
}

/**
 * Retorna apenas o símbolo da unidade
 *
 * @example
 * getLengthSymbol('kilometer');  // "km"
 * getLengthSymbol('mile');       // "mi"
 */
export function getLengthSymbol(variant: LengthUnit): string {
  const unit = LENGTH_UNITS[variant];
  if (!unit) {
    throw new Error(`Unknown length variant: ${variant}`);
  }
  return unit.symbol;
}

/**
 * Retorna o nome da unidade (singular ou plural)
 *
 * @example
 * getLengthName('kilometer', 1);   // "kilometer"
 * getLengthName('kilometer', 2);   // "kilometers"
 */
export function getLengthName(variant: LengthUnit, count: number = 1): string {
  const unit = LENGTH_UNITS[variant];
  if (!unit) {
    throw new Error(`Unknown length variant: ${variant}`);
  }
  return count === 1
    ? (unit.singular || unit.symbol)
    : (unit.plural || unit.symbol);
}
